/**
 * AccountSecurity Component — SCRUM-2240 (MVP 1.21.10 §6.4/§6.5)
 *
 * Consolidated Account Security tab. Surfaces:
 *  - Password management (Set / Change / Reset)
 *  - Connected sign-in methods (Google + Facebook) Connect / Disconnect
 *  - Lock-out warning modal that BLOCKS removal of the sole method
 *    client-side and offers a set-password CTA (§6.5)
 *  - MFA section (guide role only)
 *  - Active sessions section (guide role only)
 *
 * "Delete account" stays in its own AccountClosure tab.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Lock,
  Link as LinkIcon,
  AlertTriangle,
  CheckCircle,
  Shield,
  Monitor,
  X,
} from 'lucide-react';
import {
  getLockoutRisk,
  requestSetPassword,
  unlinkOAuthAccount,
  linkOAuthAccount,
  requestPasswordReset,
} from '../../services/authService';
import Alert from '../common/Alert';
import LoadingSpinner from '../common/LoadingSpinner';
import PasswordChange from './PasswordChange';
import MfaSettingsSection from '../mfa/MfaSettingsSection';
import SessionManagement from './SessionManagement';
import GoogleLoginButton from '../auth/GoogleLoginButton';
import FacebookLoginButton from '../auth/FacebookLoginButton';

const PROVIDER_LABELS = {
  google: 'Google',
  facebook: 'Facebook',
};

/**
 * Lock-out warning modal. When `remediations` includes `set_password`, an
 * inline CTA triggers the set-initial-password email and the user can't
 * proceed with the unlink — there is no "Disconnect anyway" button, the
 * removal is BLOCKED client-side per §6.5.
 */
function LockoutWarningModal({
  provider, risk, onCancel, onSetPassword, setPasswordState, setPasswordError,
}) {
  if (!provider || !risk) { return null; }
  const canSetPassword = (risk.remediations || []).includes('set_password');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lockout-warning-title"
      data-testid="lockout-modal"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h3 id="lockout-warning-title" className="text-lg font-semibold text-gray-900">
              Disconnecting would lock you out
            </h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-500"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-3 text-sm text-gray-700">
          <p>
            {PROVIDER_LABELS[provider] || provider} is currently your only
            sign-in method. We can&apos;t disconnect it because you&apos;d be
            unable to sign back in.
          </p>
          {canSetPassword && (
            <p>
              Set a password first — we&apos;ll email you a secure link — and
              then you can disconnect {PROVIDER_LABELS[provider] || provider}.
            </p>
          )}
          {!canSetPassword && (
            <p>
              Add a different sign-in method first, then come back and
              disconnect {PROVIDER_LABELS[provider] || provider}.
            </p>
          )}
          {setPasswordError && (
            <Alert variant="error">{setPasswordError}</Alert>
          )}
          {setPasswordState === 'sent' && (
            <Alert variant="success">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span>Check your email for a link to set your password.</span>
              </div>
            </Alert>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            data-testid="lockout-modal-cancel"
          >
            Close
          </button>
          {canSetPassword && setPasswordState !== 'sent' && (
            <button
              type="button"
              onClick={onSetPassword}
              disabled={setPasswordState === 'loading'}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              data-testid="lockout-modal-set-password"
            >
              {setPasswordState === 'loading' ? 'Sending…' : 'Email me a set-password link'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PasswordSection({ hasPassword, email, onRefresh }) {
  const [showChange, setShowChange] = useState(false);
  const [requestState, setRequestState] = useState('idle');
  const [resetState, setResetState] = useState('idle');
  const [error, setError] = useState(null);

  const handleSetPassword = async () => {
    setError(null);
    setRequestState('loading');
    try {
      await requestSetPassword();
      setRequestState('sent');
    } catch (err) {
      setRequestState('idle');
      setError(err.message || 'Failed to send set-password email');
    }
  };

  const handleResetPassword = async () => {
    setError(null);
    setResetState('loading');
    try {
      await requestPasswordReset(email);
      setResetState('sent');
    } catch (err) {
      setResetState('idle');
      setError(err.message || 'Failed to send reset email');
    }
  };

  return (
    <section className="border border-gray-200 rounded-lg p-6 bg-white">
      <div className="flex items-center gap-3 mb-4">
        <Lock className="w-5 h-5 text-gray-700" />
        <h2 className="text-lg font-semibold text-gray-900">Password</h2>
      </div>

      {error && <Alert variant="error" className="mb-4">{error}</Alert>}

      {!hasPassword && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Your account uses social sign-in only. Set a password to enable
            email + password login as a backup.
          </p>
          {requestState === 'sent' ? (
            <Alert variant="success">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span>Check your email for a link to set your password.</span>
              </div>
            </Alert>
          ) : (
            <button
              type="button"
              onClick={handleSetPassword}
              disabled={requestState === 'loading'}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {requestState === 'loading' ? 'Sending…' : 'Set a password'}
            </button>
          )}
        </div>
      )}

      {hasPassword && !showChange && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            You currently sign in with a password. You can change it any time
            or request a password-reset link by email.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setShowChange(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Change password
            </button>
            {resetState === 'sent' ? (
              <span className="self-center text-sm text-green-700">
                Reset link sent to {email}.
              </span>
            ) : (
              <button
                type="button"
                onClick={handleResetPassword}
                disabled={resetState === 'loading'}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {resetState === 'loading' ? 'Sending…' : 'Email reset link'}
              </button>
            )}
          </div>
        </div>
      )}

      {hasPassword && showChange && (
        <div className="space-y-3">
          <PasswordChange />
          <button
            type="button"
            onClick={() => { setShowChange(false); onRefresh?.(); }}
            className="text-sm text-blue-600 hover:underline"
          >
            Done
          </button>
        </div>
      )}
    </section>
  );
}

function ConnectedMethodsSection({
  hasPassword,
  providers,
  onUnlinkRequest,
  onLinkGoogle,
  onLinkFacebook,
  linkBusy,
  linkError,
}) {
  const supportedProviders = ['google', 'facebook'];
  const onlyMethod = !hasPassword && providers.length === 1;

  return (
    <section className="border border-gray-200 rounded-lg p-6 bg-white">
      <div className="flex items-center gap-3 mb-4">
        <LinkIcon className="w-5 h-5 text-gray-700" />
        <h2 className="text-lg font-semibold text-gray-900">
          Connected sign-in methods
        </h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Manage which providers can sign in to your account. We&apos;ll prevent
        you from removing your last sign-in method.
      </p>

      {linkError && <Alert variant="error" className="mb-4">{linkError}</Alert>}

      <ul className="divide-y divide-gray-200">
        {supportedProviders.map((provider) => {
          const isConnected = providers.includes(provider);
          const isSole = isConnected && onlyMethod && providers[0] === provider;
          return (
            <li
              key={provider}
              className="py-4 flex items-center justify-between gap-4"
              data-testid={`provider-row-${provider}`}
            >
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {PROVIDER_LABELS[provider]}
                </p>
                <p className="text-xs text-gray-500">
                  {isConnected ? 'Connected' : 'Not connected'}
                  {isSole && (
                    <span className="ml-2 text-amber-600">
                      · Only sign-in method
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isConnected && (
                  <button
                    type="button"
                    onClick={() => onUnlinkRequest(provider)}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    data-testid={`disconnect-${provider}`}
                  >
                    Disconnect
                  </button>
                )}
                {!isConnected && provider === 'google' && (
                  <div className="w-56" data-testid="connect-google">
                    <GoogleLoginButton
                      onSuccess={(resp) => onLinkGoogle(resp?.credential)}
                      onError={() => onLinkGoogle(null)}
                      disabled={linkBusy}
                    />
                  </div>
                )}
                {!isConnected && provider === 'facebook' && (
                  <div className="w-56" data-testid="connect-facebook">
                    <FacebookLoginButton
                      onSuccess={(resp) => onLinkFacebook(resp?.accessToken)}
                      onError={() => onLinkFacebook(null)}
                      disabled={linkBusy}
                    />
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function AccountSecurity({ accountData }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [methods, setMethods] = useState({ hasPassword: false, providers: [] });
  const [pendingUnlink, setPendingUnlink] = useState(null); // provider name
  const [pendingRisk, setPendingRisk] = useState(null);     // assessLockoutRisk response
  const [unlinking, setUnlinking] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [modalSetPwState, setModalSetPwState] = useState('idle');
  const [modalSetPwError, setModalSetPwError] = useState(null);

  const userRole = accountData?.user_type || accountData?.role || 'customer';
  const email = accountData?.email || '';

  const refresh = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      // Current state (no specific action): hasPassword + otherProviders
      const risk = await getLockoutRisk();
      setMethods({
        hasPassword: Boolean(risk?.hasPassword),
        providers: Array.isArray(risk?.otherProviders) ? risk.otherProviders : [],
      });
    } catch (err) {
      setError(err.message || 'Failed to load account security state');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleUnlinkRequest = async (provider) => {
    setLinkError(null);
    try {
      // Ask the backend for the risk-assessment of THIS specific unlink.
      const risk = await getLockoutRisk(`unlink_${provider}`);
      if (risk?.wouldLockOut) {
        // BLOCK: open the modal — no "disconnect anyway" path exists.
        setModalSetPwState('idle');
        setModalSetPwError(null);
        setPendingRisk(risk);
        setPendingUnlink(provider);
        return;
      }
      await performUnlink(provider);
    } catch (err) {
      setError(err.message || `Failed to check lock-out risk for ${provider}`);
    }
  };

  const performUnlink = async (provider) => {
    setUnlinking(true);
    setError(null);
    try {
      await unlinkOAuthAccount(provider);
      await refresh();
    } catch (err) {
      setError(err.message || `Failed to disconnect ${provider}`);
    } finally {
      setUnlinking(false);
    }
  };

  const handleModalSetPassword = async () => {
    setModalSetPwError(null);
    setModalSetPwState('loading');
    try {
      await requestSetPassword();
      setModalSetPwState('sent');
    } catch (err) {
      setModalSetPwState('idle');
      setModalSetPwError(err.message || 'Failed to send set-password email');
    }
  };

  const closeModal = () => {
    setPendingUnlink(null);
    setPendingRisk(null);
    setModalSetPwState('idle');
    setModalSetPwError(null);
  };

  const handleLinkGoogle = async (credential) => {
    if (!credential) {
      setLinkError('Google sign-in was cancelled or failed.');
      return;
    }
    setLinkBusy(true);
    setLinkError(null);
    try {
      await linkOAuthAccount('google', credential);
      await refresh();
    } catch (err) {
      setLinkError(err.message || 'Failed to link Google account');
    } finally {
      setLinkBusy(false);
    }
  };

  const handleLinkFacebook = async (accessToken) => {
    if (!accessToken) {
      setLinkError('Facebook sign-in was cancelled or failed.');
      return;
    }
    setLinkBusy(true);
    setLinkError(null);
    try {
      await linkOAuthAccount('facebook', accessToken);
      await refresh();
    } catch (err) {
      setLinkError(err.message || 'Failed to link Facebook account');
    } finally {
      setLinkBusy(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading security settings…" size="md" />;
  }

  return (
    <div className="space-y-6">
      {error && <Alert variant="error">{error}</Alert>}

      <PasswordSection
        hasPassword={methods.hasPassword}
        email={email}
        onRefresh={refresh}
      />

      <ConnectedMethodsSection
        hasPassword={methods.hasPassword}
        providers={methods.providers}
        onUnlinkRequest={handleUnlinkRequest}
        onLinkGoogle={handleLinkGoogle}
        onLinkFacebook={handleLinkFacebook}
        linkBusy={linkBusy || unlinking}
        linkError={linkError}
      />

      {userRole === 'guide' && (
        <section
          className="border border-gray-200 rounded-lg p-6 bg-white"
          data-testid="mfa-section"
        >
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">
              Multi-factor authentication
            </h2>
          </div>
          <MfaSettingsSection />
        </section>
      )}

      {userRole === 'guide' && (
        <section
          className="border border-gray-200 rounded-lg p-6 bg-white"
          data-testid="sessions-section"
        >
          <div className="flex items-center gap-3 mb-4">
            <Monitor className="w-5 h-5 text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">
              Active sessions
            </h2>
          </div>
          <SessionManagement />
        </section>
      )}

      <LockoutWarningModal
        provider={pendingUnlink}
        risk={pendingRisk}
        onCancel={closeModal}
        onSetPassword={handleModalSetPassword}
        setPasswordState={modalSetPwState}
        setPasswordError={modalSetPwError}
      />
    </div>
  );
}
